/**
 * @fileoverview Practice reminder settings in the student portal (F5 / Spec 03).
 *
 * Three things this card has to get right.
 *
 * **It hides itself when the feature is off.** The settings endpoint 404s
 * behind `REMINDERS_ENABLED`, and this component renders nothing until it has
 * resolved — pattern (1) from the runtime-flag note, which is what lets a flag
 * flip reach an already-shipped build with nothing to keep in sync.
 *
 * **The hour select offers whole hours only**, because the sweep is hourly.
 * Offering 4:30 and firing at 4:00 would be a lie the architecture cannot make
 * true; an honest control is cheaper than an honest label over a dishonest one.
 *
 * **It reads the schedule back.** `next_fire` is shown in plain language, so a
 * student can see that the timezone we stored is the one they are standing in
 * — the single failure mode of a scheduled feature that nobody would otherwise
 * notice until the reminder arrived at 3am.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import studentApi from "@/lib/studentApi";
import { getErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, Eyebrow } from "@/components/studio/primitives";
import { SToggle } from "@/components/studio/studentUi";

const DAYS = [
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
  { key: "sun", label: "S" },
];

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: new Date(2020, 0, 1, h).toLocaleTimeString(undefined, { hour: "numeric" }),
}));

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

function nextFireLabel(iso) {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString(undefined, { weekday: "long", hour: "numeric", minute: "2-digit" });
}

export function PracticeRemindersCard() {
  const [available, setAvailable] = useState(null); // null = still asking
  const [settings, setSettings] = useState(null);
  const [nextFire, setNextFire] = useState(null);
  const [hasDevice, setHasDevice] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    studentApi
      .get("/student/reminder-settings")
      .then(({ data }) => {
        if (!alive) return;
        setSettings(data.settings);
        setNextFire(data.next_fire);
        setHasDevice(data.has_device !== false);
        setAvailable(true);
      })
      .catch(() => alive && setAvailable(false));
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(async (next) => {
    setSaving(true);
    // The stored timezone follows the device the student is actually using —
    // a family that moves house should not have to find this setting.
    const payload = { ...next, timezone: detectTimezone() };
    try {
      const { data } = await studentApi.put("/student/reminder-settings", payload);
      setSettings(data.settings);
      setNextFire(data.next_fire);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't save your reminders"));
    } finally {
      setSaving(false);
    }
  }, []);

  if (!available || !settings) return null;

  const toggleDay = (key) => {
    const days = settings.days.includes(key)
      ? settings.days.filter((d) => d !== key)
      : [...settings.days, key];
    setSettings((s) => ({ ...s, days }));
    // Turning off the last day would be a schedule that can never fire, which
    // the server refuses — so it turns the whole thing off instead, which is
    // what the student meant.
    save({ ...settings, days, enabled: days.length ? settings.enabled : false });
  };

  const label = nextFireLabel(nextFire);

  return (
    <Card className="mb-[22px]" data-testid="practice-reminders">
      <Eyebrow icon="bell">Practice reminders</Eyebrow>

      <div className="mt-1.5 flex items-center gap-4 py-4">
        <div className="flex-1">
          <div className="font-sans text-base font-medium text-[var(--text-primary)]">Remind me to practise</div>
          <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            A nudge on your phone. Nothing arrives on a day you&rsquo;ve already practised.
          </div>
        </div>
        <SToggle
          on={settings.enabled}
          onToggle={() => save({ ...settings, enabled: !settings.enabled })}
          ariaLabel="Practice reminders"
        />
      </div>

      {settings.enabled ? (
        <div className="border-t border-[var(--studio-divider)] pt-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="reminder-days">
            {DAYS.map((d) => {
              const on = settings.days.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => toggleDay(d.key)}
                  disabled={saving}
                  aria-pressed={on}
                  aria-label={d.key}
                  className={cn(
                    // Tinted rather than solid yellow: seven filled circles
                    // would be the loudest use of the accent anywhere in the
                    // product and would compete with the one global Record
                    // CTA. Matches the lesson-type toggles on SchedulePage.
                    "h-9 w-9 rounded-full border font-mono text-[13px] transition-colors",
                    on
                      ? "border-[var(--accent-yellow)] bg-[var(--accent-yellow-dim)] text-[var(--text-primary)]"
                      : "border-[var(--studio-divider-strong)] text-[var(--text-secondary)]",
                  )}
                >
                  {d.label}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label htmlFor="reminder-hour" className="font-sans text-[13px] text-[var(--text-secondary)]">
              At
            </label>
            <select
              id="reminder-hour"
              data-testid="reminder-hour"
              value={settings.hour_local}
              onChange={(e) => save({ ...settings, hour_local: Number(e.target.value) })}
              className="rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
            >
              {HOURS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </div>

          {label ? (
            <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="reminder-next">
              Next reminder {label}.
            </p>
          ) : null}

          {!hasDevice ? (
            <p className="mt-3 font-sans text-[13px] text-[var(--text-secondary)]" data-testid="reminder-no-device">
              Reminders arrive on your phone, so install the Hans app and sign in there to start getting them.
            </p>
          ) : null}
        </div>
      ) : null}

    </Card>
  );
}

export default PracticeRemindersCard;
